import { Stack } from '@mui/material';
import { type StandardSchemaV1Issue, useStore } from '@tanstack/react-form';
import { get, merge } from 'lodash';
import { useCallback, useMemo } from 'react';
import { handleArrayPaste } from '../pa-formik/utils';
import { BaseTextField, type BaseTextFieldProps } from './BaseTextField';
import { useFieldContext } from './formContext';

interface Props extends Pick<BaseTextFieldProps, 'numberFormat' | 'size' | 'variant' | 'disabled'> {
  type?: React.HTMLInputTypeAttribute;
  layout?: 'column' | 'grid';
  gap?: number;
  fieldSx?: BaseTextFieldProps['sx'];
  onPasteAtIndex?: (e: React.ClipboardEvent<HTMLDivElement>, index: number) => void;
}

function FormArrayTextField({
  type,
  numberFormat,
  size,
  variant,
  layout = 'column',
  gap = 1,
  fieldSx,
  onPasteAtIndex,
}: Readonly<Props>) {
  const field = useFieldContext<Array<string>>();
  const terminalName = field.name.split('.').at(-1);
  // have to use `useStore` here because `field.state.value` is not reactive
  // and will not update when pasting
  const value = useStore(field.form.store, (state) =>
    get(state.values, field.name),
  ) as Array<string>;

  const onFieldChange = useCallback(
    (fieldVal: string, index: number) => {
      const newArr = value.slice();
      newArr[index] = fieldVal;
      field.handleChange(newArr);
    },
    [value, field],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>, i: number) => {
      if (onPasteAtIndex) {
        onPasteAtIndex(e, i);
        if (e.defaultPrevented) return;
      }
      const newArr = handleArrayPaste(e, i, value);
      field.handleChange(newArr as Array<string>);
    },
    [value, field, onPasteAtIndex],
  );

  const errorMap: Record<string, StandardSchemaV1Issue> = useMemo(
    () => merge(field.state.meta.errorMap.onMount, field.state.meta.errorMap.onChange),
    [field.state.meta.errorMap.onMount, field.state.meta.errorMap.onChange],
  );

  return (
    <Stack
      spacing={layout === 'column' ? gap : 0}
      sx={
        layout === 'grid'
          ? {
              display: 'grid',
              gridTemplateColumns: `repeat(${value.length}, minmax(0, 1fr))`,
              columnGap: gap,
              rowGap: gap,
            }
          : undefined
      }
    >
      {value.map((_, i) => {
        const error = Object.values(errorMap).find((issue) => issue.path?.[0] === i);
        return (
          <BaseTextField
            // biome-ignore lint/suspicious/noArrayIndexKey: fine
            key={`array-field-${terminalName}-${i}`}
            value={value[i]}
            onChange={(e) => onFieldChange(e.target.value, i)}
            type={type}
            error={Boolean(error)}
            helperText={error?.message.includes('expected number') ? '' : error?.message}
            numberFormat={numberFormat}
            size={size}
            variant={variant ?? 'standard'}
            onPaste={(e) => handlePaste(e, i)}
            data-testid={`${terminalName}-${i}`}
            textAlign="right"
            sx={layout === 'column' ? { pb: 0.5, ...fieldSx } : fieldSx}
          />
        );
      })}
    </Stack>
  );
}

export default FormArrayTextField;
